/**
 * Ações da IA do atendimento (`social_ai_actions`) na tela: o texto humano de
 * cada uma, o que dá para desfazer e o motivo de uma falha. Fase 2 do atendente
 * (~/.claude/plans/squishy-twirling-hanrahan.md; doc
 * docs/funcionalidades/atendente-ia-whatsapp.md).
 *
 * Tolerante de propósito: a `entrada` é o pedido validado pelo servidor e cada
 * ferramenta tem o seu formato (as de pré-reserva/experimental chegam na Fase
 * 1b). Campo ausente some do texto; nunca aparece JSON nem id cru.
 */
import { formatCurrency } from '@/lib/constants';

export const FERRAMENTAS_DA_IA = [
  'atualizar_lead',
  'criar_oportunidade',
  'pre_reservar_quadra',
  'pre_agendar_experimental',
  'chamar_atendente',
] as const;
export type FerramentaDaIa = (typeof FERRAMENTAS_DA_IA)[number];

export type StatusDaAcaoDaIa =
  | 'proposta'
  | 'executando'
  | 'executada'
  | 'recusada'
  | 'falhou'
  | 'desfazendo'
  | 'desfeita';

export interface AcaoDaIa {
  id: string;
  ferramenta: string;
  status: StatusDaAcaoDaIa;
  entrada: Record<string, unknown>;
  resultado: Record<string, unknown>;
  erro: string | null;
  created_at: string;
  executada_em: string | null;
  /** Criada nas últimas horas (decide se o cartão aparece no chat). Calculado na leitura. */
  recente: boolean;
  /**
   * Em executando/desfazendo há mais de PRAZO_PARA_LIBERAR_MS (worker morreu no
   * meio): a tela mostra "Interrompida" com o botão Liberar, não o girando.
   * Calculado na leitura.
   */
  parada: boolean;
}

/** Mesmo prazo da Edge ia-acoes (`PRAZO_PARA_LIBERAR_MS` em _shared/ia-ferramentas/decisao.ts). */
export const PRAZO_PARA_LIBERAR_MS = 5 * 60 * 1000;

/** Ferramentas com `desfazer` no servidor (`ACOES` em _shared/ia-ferramentas). */
const REVERSIVEIS: ReadonlySet<string> = new Set([
  'atualizar_lead',
  'criar_oportunidade',
  'pre_reservar_quadra',
  'pre_agendar_experimental',
]);

/** Ainda em curso: a tela mostra "executando…" e não oferece botão. */
export const STATUS_EM_CURSO: ReadonlySet<StatusDaAcaoDaIa> = new Set(['executando', 'desfazendo']);

type T = (chave: string, opcoes?: Record<string, unknown>) => string;

/** O que vira cartão no chat: pendentes e em curso sempre; executadas/falhas só as recentes. */
export function acoesDoChat(acoes: AcaoDaIa[]): AcaoDaIa[] {
  return acoes.filter((a) =>
    a.status === 'proposta' ||
    STATUS_EM_CURSO.has(a.status) ||
    (a.recente && (a.status === 'executada' || a.status === 'falhou')),
  );
}

/**
 * O botão "Desfazer" aparece? O servidor é quem decide (e recusa com motivo);
 * aqui só se esconde o que certamente não volta: oportunidade que já existia e
 * atualização antiga sem os valores de antes.
 */
export function podeDesfazer(a: Pick<AcaoDaIa, 'ferramenta' | 'status' | 'resultado'>): boolean {
  if (a.status !== 'executada' || !REVERSIVEIS.has(a.ferramenta)) return false;
  if (a.ferramenta === 'criar_oportunidade') return a.resultado?.ja_existia === false;
  if (a.ferramenta === 'atualizar_lead') return !!a.resultado?.para_desfazer;
  return true;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function texto(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  return s && !UUID.test(s) ? s : null;
}

function primeiro(o: Record<string, unknown> | undefined, chaves: string[]): string | null {
  for (const k of chaves) {
    const v = texto(o?.[k]);
    if (v) return v;
  }
  return null;
}

/** 'YYYY-MM-DD' → 'dd/MM'; outro formato passa como veio. */
function dia(v: string | null): string | null {
  const m = v?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}` : v;
}

/** 'HH:MM[:SS]' → 'HH:MM'. */
function hora(v: string | null): string | null {
  const m = v?.match(/^(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : v;
}

/** Instante ISO com fuso → 'dd/MM HH:mm' no fuso do clube. Data simples cai em `dia`. */
function quando(v: string | null): string | null {
  if (!v) return null;
  if (!/T\d{2}:\d{2}/.test(v)) return dia(v);
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const p = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(d);
  const x = (tipo: string) => p.find((q) => q.type === tipo)?.value ?? '';
  return `${x('day')}/${x('month')} ${x('hour')}:${x('minute')}`;
}

function valor(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? formatCurrency(n) : null;
}

function detalhesDoLead(e: Record<string, unknown>, t: T): string[] {
  const partes: string[] = [];
  const nome = texto(e.name);
  if (nome) partes.push(t('whatsapp.iaAcoes.campos.nome', { valor: nome }));
  const email = texto(e.email);
  if (email) partes.push(t('whatsapp.iaAcoes.campos.email', { valor: email }));
  if (e.sport_id) partes.push(t('whatsapp.iaAcoes.campos.esporte'));
  const nivel = texto(e.level);
  if (nivel && ['beginner', 'intermediate', 'advanced', 'pro'].includes(nivel)) {
    partes.push(t('whatsapp.iaAcoes.campos.nivel', { valor: t(`whatsapp.iaAcoes.niveis.${nivel}`) }));
  }
  const obs = texto(e.observacao);
  if (obs) partes.push(t('whatsapp.iaAcoes.campos.observacao', { valor: obs }));
  return partes;
}

function detalhesDaOportunidade(e: Record<string, unknown>, t: T): string[] {
  // `rotulo` vem do servidor como "plano <nome>" / "torneio <nome>".
  const nome = texto(e.rotulo)?.replace(/^(plano|torneio)\s+/i, '') ?? null;
  const tipo = texto(e.product_type);
  let produto: string | null = null;
  if (e.tuition_plan_id && nome) produto = t('whatsapp.iaAcoes.oportunidadePlano', { nome });
  else if (e.tournament_id && nome) produto = t('whatsapp.iaAcoes.oportunidadeTorneio', { nome });
  else if (tipo) produto = t(`constants.productTypes.${tipo}`);
  const preco = valor(e.estimated_value);
  if (!produto) return preco ? [preco] : [];
  return [preco ? t('whatsapp.iaAcoes.comValor', { texto: produto, valor: preco }) : produto];
}

function detalhesDaPreReserva(e: Record<string, unknown>, r: Record<string, unknown>): string[] {
  const quadra = primeiro(e, ['quadra_nome', 'quadra', 'court_name', 'nome_da_quadra']) ?? primeiro(r, ['quadra_nome', 'court_name']);
  const data = dia(primeiro(e, ['data', 'date', 'booking_date']));
  const inicio = hora(primeiro(e, ['inicio', 'start_time', 'hora_inicio']));
  const fim = hora(primeiro(e, ['fim', 'end_time', 'hora_fim']));
  const faixa = inicio && fim ? `${inicio}–${fim}` : inicio;
  const momento = [data, faixa].filter(Boolean).join(' ');
  return [quadra, momento || null].filter((x): x is string => !!x);
}

function detalhesDaExperimental(e: Record<string, unknown>): string[] {
  const dataHora = primeiro(e, ['data_hora', 'scheduled_at', 'inicio_em']);
  const momento = dataHora
    ? quando(dataHora)
    : [dia(primeiro(e, ['data', 'date'])), hora(primeiro(e, ['hora', 'inicio', 'start_time']))].filter(Boolean).join(' ') || null;
  const esporte = primeiro(e, ['esporte_nome', 'esporte', 'sport_name']);
  return [momento, esporte].filter((x): x is string => !!x);
}

/** Texto humano da ação: "Criar oportunidade: plano Trimestral — R$ 275,00". */
export function descreverAcao(a: Pick<AcaoDaIa, 'ferramenta' | 'entrada' | 'resultado'>, t: T): string {
  const e = (a.entrada ?? {}) as Record<string, unknown>;
  const r = (a.resultado ?? {}) as Record<string, unknown>;
  const conhecida = (FERRAMENTAS_DA_IA as readonly string[]).includes(a.ferramenta);
  const titulo = t(conhecida ? `whatsapp.iaAcoes.titulos.${a.ferramenta}` : 'whatsapp.iaAcoes.titulos.outra');
  let partes: string[] = [];
  switch (a.ferramenta) {
    case 'atualizar_lead': partes = detalhesDoLead(e, t); break;
    case 'criar_oportunidade': partes = detalhesDaOportunidade(e, t); break;
    case 'pre_reservar_quadra': partes = detalhesDaPreReserva(e, r); break;
    case 'pre_agendar_experimental': partes = detalhesDaExperimental(e); break;
    case 'chamar_atendente': partes = [texto(e.motivo)].filter((x): x is string => !!x); break;
  }
  return partes.length ? t('whatsapp.iaAcoes.comDetalhe', { titulo, detalhes: partes.join(' · ') }) : titulo;
}

/** Motivos que a tela traduz (`whatsapp.iaAcoes.motivos.<codigo>`); o resto vira o genérico com o código. */
export const MOTIVOS_CONHECIDOS: ReadonlySet<string> = new Set([
  'ja_decidida',
  'ferramenta_desligada',
  'interrompida',
  'ainda_em_curso',
  'nao_esta_em_curso',
  'nao_executada',
  'nao_reversivel',
  'ferramenta_desconhecida',
  'erro_interno',
  'lead_nao_encontrado',
  'lead_alterado_depois',
  'sem_lead',
  'oportunidade_alterada',
  'oportunidade_em_uso',
  'aviso_falhou',
]);

/** Texto do motivo de uma falha (código curto do servidor ou da trilha). */
export function textoDoMotivo(codigo: string | null | undefined, t: T): string {
  const c = (codigo ?? '').trim();
  if (MOTIVOS_CONHECIDOS.has(c)) return t(`whatsapp.iaAcoes.motivos.${c}`);
  return t('whatsapp.iaAcoes.motivos.desconhecido', { codigo: c || '—' });
}
